/**
 * fetch-retry — résilience réseau pour les appels API.
 *
 * Retente une requête en cas d'échec RÉSEAU transitoire (`TypeError: Failed to fetch` :
 * le navigateur n'a pas réussi à joindre le serveur — perte de connexion, hoquet réseau).
 * Beaucoup de ces échecs réussissent au 2ᵉ essai → l'utilisateur ne voit plus l'erreur
 * et ça réduit le bruit côté Sentry (cf. BIZCOACH-T).
 *
 * ⚠️ On ne retente QUE les requêtes idempotentes (GET/HEAD) : rejouer un POST/PUT/DELETE
 * risquerait un double effet de bord (double création, etc.).
 *
 * Module isolé (sans dépendance) pour être testable sans tirer toute la chaîne front.
 */
export interface FetchRetryOptions {
  retries?: number; // nombre de tentatives supplémentaires (def. 2)
  backoffMs?: number; // délai de base entre tentatives, croît linéairement (def. 400)
  fetchFn?: typeof fetch; // injectable pour les tests
}

export async function fetchWithRetry(
  url: string,
  init: RequestInit = {},
  opts: FetchRetryOptions = {},
): Promise<Response> {
  const retries = opts.retries ?? 2;
  const backoffMs = opts.backoffMs ?? 400;
  const doFetch = opts.fetchFn ?? fetch;
  const method = (init.method ?? "GET").toUpperCase();
  const idempotent = method === "GET" || method === "HEAD";

  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await doFetch(url, init);
    } catch (err) {
      lastErr = err;
      // "Failed to fetch" se présente comme un TypeError côté navigateur.
      const isNetworkError = err instanceof TypeError;
      if (!isNetworkError || !idempotent || attempt === retries) break;
      await new Promise((resolve) => setTimeout(resolve, backoffMs * (attempt + 1)));
    }
  }
  throw lastErr;
}
